import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, ArrowRight, Download, Mail, Phone, Calendar } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const txnid = searchParams.get('txnid') || 'N/A';
  const status = searchParams.get('status') || 'success';
  const amount = searchParams.get('amount') || '20.00';
  const mihpayid = searchParams.get('mihpayid') || '';

  useEffect(() => {
    // Track successful payment (you can add analytics here)
    console.log('Payment successful:', { txnid, status, amount, mihpayid });
  }, [txnid, status, amount, mihpayid]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100">
      {/* Success Hero */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #059669 0%, #047857 100%)' }}>
        {/* Confetti Animation */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute"
              style={{
                left: `${Math.random() * 100}%`,
                top: `-20px`,
                width: '10px',
                height: '10px',
                backgroundColor: ['#ffd700', '#ff6b6b', '#4ecdc4', '#fff'][Math.floor(Math.random() * 4)],
                animation: `confetti ${Math.random() * 3 + 2}s ease-out infinite`,
                animationDelay: `${Math.random() * 2}s`
              }}
            />
          ))}
        </div>

        <div className="container mx-auto max-w-4xl px-6 py-20 relative z-10 text-center">
          {/* Success Icon */}
          <div className="inline-flex items-center justify-center w-24 h-24 bg-white rounded-full mb-8 shadow-2xl">
            <CheckCircle2 className="w-14 h-14 text-green-500" />
          </div>
          
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4">
            🎉 Payment Successful!
          </h1>
          <p className="text-xl text-green-100 max-w-2xl mx-auto">
            Congratulations! Your seat has been successfully booked for the CSDGPM program.
          </p>
        </div>

        {/* CSS for confetti */}
        <style>{`
          @keyframes confetti {
            0% { transform: translateY(-20px) rotate(0deg); opacity: 1; }
            100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
          }
        `}</style>
      </div>

      {/* Transaction Details */}
      <div className="container mx-auto max-w-4xl px-6 -mt-10 relative z-20">
        <Card className="shadow-2xl border-0">
          <CardContent className="p-8">
            {/* Transaction Info */}
            <div className="bg-green-50 rounded-xl p-6 mb-8 border border-green-200">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                Transaction Details
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Transaction ID</p>
                  <p className="font-mono font-bold text-gray-900" data-testid="txn-id">{txnid}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Amount Paid</p>
                  <p className="font-bold text-green-600 text-2xl" data-testid="amount-paid">${amount} USD</p>
                </div>
                {mihpayid && (
                  <div>
                    <p className="text-sm text-gray-500">PayU Reference</p>
                    <p className="font-mono text-gray-900">{mihpayid}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                    <CheckCircle2 className="w-4 h-4" />
                    Confirmed
                  </span>
                </div>
              </div>
            </div>

            {/* What's Next */}
            <div className="mb-8">
              <h2 className="text-lg font-bold text-gray-900 mb-4">What Happens Next?</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-lg">
                  <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">1</div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Confirmation Email</h3>
                    <p className="text-sm text-gray-600">You'll receive a confirmation email with your enrollment details within the next few minutes.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-lg">
                  <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">2</div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Team Contact</h3>
                    <p className="text-sm text-gray-600">Our team will reach out to you within 24 hours to welcome you and share batch details.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-lg">
                  <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">3</div>
                  <div>
                    <h3 className="font-semibold text-gray-900">New Batches Every Month</h3>
                    <p className="text-sm text-gray-600">Get ready to start your digital marketing journey! Balance fee to be paid before batches running.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Course Summary */}
            <div className="bg-gray-50 rounded-xl p-6 mb-8">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Your Course</h2>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900">CSDGPM - Digital & Performance Marketing</h3>
                  <p className="text-sm text-gray-500">6 Months • 150 Live Hours • Exemplar Global Certified</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Seat Booking</p>
                  <p className="text-xl font-bold text-green-600">$20 USD</p>
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="border-t border-gray-200 pt-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Need Help?</h2>
              <div className="flex flex-wrap gap-4">
                <a href="mailto:shrey@workshoplibrary.com" className="flex items-center gap-2 text-blue-600 hover:text-blue-700">
                  <Mail className="w-5 h-5" />
                  shrey@workshoplibrary.com
                </a>
                <a href="https://wa.me/919891550666" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-green-600 hover:text-green-700">
                  <Phone className="w-5 h-5" />
                  WhatsApp Support
                </a>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <Button
                onClick={() => navigate('/')}
                className="flex-1 py-6 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                data-testid="back-to-home"
              >
                <ArrowRight className="w-5 h-5 mr-2" />
                Explore More Courses
              </Button>
              <Button
                onClick={() => navigate('/csdgpm-offer')}
                variant="outline"
                className="flex-1 py-6"
              >
                <Calendar className="w-5 h-5 mr-2" />
                View Course Details
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <footer className="py-8 px-6 text-center mt-16">
        <p className="text-sm text-gray-600">© 2026 AI School for All. All rights reserved.</p>
        <p className="text-xs text-gray-400 mt-2">Powered by Workshop Library</p>
      </footer>
    </div>
  );
};

export default PaymentSuccess;
